import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { api } from '@/services/api'

export interface AutocompleteOptions {
  /**
   * Minimum characters before showing suggestions
   */
  minChars: number
  /**
   * Debounce time in milliseconds
   */
  debounce: number
  /**
   * Character to accept suggestion (default: Tab)
   */
  acceptKey: string
}

interface AutocompleteState {
  active: boolean
  suggestion: string | null
  prefix: string
  from: number
  to: number
  decorations: DecorationSet
}

const autocompletePluginKey = new PluginKey<AutocompleteState>('autocomplete')

// Regex to match Amharic word at cursor
const AMHARIC_WORD_END_REGEX = /[\u1200-\u137F\u1380-\u139F\u2D80-\u2DDF]+$/

export const AutocompleteExtension = Extension.create<AutocompleteOptions>({
  name: 'autocomplete',

  addOptions() {
    return {
      minChars: 2,
      debounce: 300,
      acceptKey: 'Tab'
    }
  },

  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        const state = autocompletePluginKey.getState(editor.state)
        if (state?.active && state.suggestion) {
          // Accept the suggestion
          const { from, to, suggestion, prefix } = state
          const completion = suggestion.slice(prefix.length)

          editor
            .chain()
            .focus()
            .deleteRange({ from, to })
            .insertContentAt(from, prefix + completion)
            .run()

          return true
        }
        return false
      },
      Escape: ({ editor }) => {
        const state = autocompletePluginKey.getState(editor.state)
        if (state?.active) {
          // Clear the suggestion
          const tr = editor.state.tr.setMeta(autocompletePluginKey, {
            active: false,
            suggestion: null,
            prefix: '',
            from: 0,
            to: 0,
            decorations: DecorationSet.empty
          })
          editor.view.dispatch(tr)
          return true
        }
        return false
      },
      Space: ({ editor }) => {
        // Clear autocomplete suggestion on space (auto-clean)
        const state = autocompletePluginKey.getState(editor.state)
        if (state?.active) {
          const tr = editor.state.tr.setMeta(autocompletePluginKey, {
            active: false,
            suggestion: null,
            prefix: '',
            from: 0,
            to: 0,
            decorations: DecorationSet.empty
          })
          editor.view.dispatch(tr)
        }
        // Return false to allow normal space insertion
        return false
      }
    }
  },

  addProseMirrorPlugins() {
    const { minChars, debounce } = this.options
    let timeoutId: NodeJS.Timeout | null = null
    let lastPrefix = ''

    return [
      new Plugin<AutocompleteState>({
        key: autocompletePluginKey,

        state: {
          init() {
            return {
              active: false,
              suggestion: null,
              prefix: '',
              from: 0,
              to: 0,
              decorations: DecorationSet.empty
            }
          },

          apply(tr, oldState) {
            const meta = tr.getMeta(autocompletePluginKey)
            if (meta) {
              return meta
            }

            // Map decorations if document changed
            if (tr.docChanged) {
              return {
                ...oldState,
                decorations: oldState.decorations.map(tr.mapping, tr.doc)
              }
            }

            return oldState
          }
        },

        props: {
          decorations(state) {
            return this.getState(state)?.decorations ?? DecorationSet.empty
          }
        },

        view(view) {
          const fetchSuggestion = async (prefix: string, from: number, to: number) => {
            if (prefix === lastPrefix) return
            lastPrefix = prefix

            try {
              const result = await api.autocomplete(prefix, 1)

              if (result.suggestions.length > 0) {
                const suggestion = result.suggestions[0]

                // Only show if suggestion is different from prefix
                if (suggestion !== prefix && suggestion.startsWith(prefix)) {
                  const completion = suggestion.slice(prefix.length)

                  // Create ghost text decoration
                  const ghostDecoration = Decoration.widget(to, () => {
                    const span = document.createElement('span')
                    span.className = 'autocomplete-ghost'
                    span.textContent = completion
                    span.setAttribute('data-suggestion', suggestion)
                    return span
                  }, { side: 1 })

                  const tr = view.state.tr.setMeta(autocompletePluginKey, {
                    active: true,
                    suggestion,
                    prefix,
                    from,
                    to,
                    decorations: DecorationSet.create(view.state.doc, [ghostDecoration])
                  })
                  view.dispatch(tr)
                } else {
                  // Clear suggestions
                  clearSuggestion()
                }
              } else {
                clearSuggestion()
              }
            } catch (error) {
              console.error('Autocomplete error:', error)
              clearSuggestion()
            }
          }

          const clearSuggestion = () => {
            const currentState = autocompletePluginKey.getState(view.state)
            if (currentState?.active) {
              const tr = view.state.tr.setMeta(autocompletePluginKey, {
                active: false,
                suggestion: null,
                prefix: '',
                from: 0,
                to: 0,
                decorations: DecorationSet.empty
              })
              view.dispatch(tr)
            }
          }

          const checkForAutocomplete = () => {
            const { state } = view
            const { selection, doc } = state
            const { from } = selection

            // Only work with cursor (not range selection)
            if (!selection.empty) {
              clearSuggestion()
              return
            }

            // Get text before cursor
            const textBefore = doc.textBetween(Math.max(0, from - 50), from, '')

            // Find Amharic word at cursor
            const match = textBefore.match(AMHARIC_WORD_END_REGEX)

            if (match && match[0].length >= minChars) {
              const prefix = match[0]
              const wordFrom = from - prefix.length

              // Debounce the API call
              if (timeoutId) {
                clearTimeout(timeoutId)
              }
              timeoutId = setTimeout(() => {
                fetchSuggestion(prefix, wordFrom, from)
              }, debounce)
            } else {
              clearSuggestion()
              lastPrefix = ''
            }
          }

          return {
            update(view, prevState) {
              // Check if cursor position or text changed
              if (
                view.state.selection.from !== prevState.selection.from ||
                !view.state.doc.eq(prevState.doc)
              ) {
                checkForAutocomplete()
              }
            },

            destroy() {
              if (timeoutId) {
                clearTimeout(timeoutId)
              }
            }
          }
        }
      })
    ]
  }
})

// Helper to check if autocomplete is active
export function isAutocompleteActive(state: any): boolean {
  const pluginState = autocompletePluginKey.getState(state)
  return pluginState?.active ?? false
}

// Helper to get current suggestion
export function getCurrentSuggestion(state: any): string | null {
  const pluginState = autocompletePluginKey.getState(state)
  return pluginState?.suggestion ?? null
}
